/**
 * EVO — KIRUVCHI YUK: ta'minotchi bizga yozgan xabarnomalar (`/api/partner-supply`).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ BU MA'LUMOT QATLAMI PARTNERNING TOMONIDA TURADI                          ║
 * ║                                                                          ║
 * ║ Xabarnomani BIZ yozmaymiz — u bizga YOZILADI: ta'minotchimiz             ║
 * ║ hisob-faktura chiqarganda o'sha hujjat bizning bazamizga xabarnoma       ║
 * ║ bo'lib tushadi. Shuning uchun bu faylda «yuborish» funksiyasi YO'Q va    ║
 * ║ bo'lishi ham mumkin emas: serverda unday endpoint ataylab ochilmagan.    ║
 * ║                                                                          ║
 * ║ Ya'ni bu yerda faqat uchta amal bor va uchtasi ham TORTISH:              ║
 * ║   • ro'yxat  — «menga nima kelyapti?»                                    ║
 * ║   • bittasi  — satrlari bilan                                            ║
 * ║   • qabul    — «qaysi omborimga tushdi?»                                 ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ TANNARX FAQAT KELADI — HECH QACHON YUBORILMAYDI                          │
 * │                                                                          │
 * │ `PartnerSupplyLine.unitCost` — ta'minotchining bizga yozgan narxi va u   │
 * │ xabarnomada QOTIB qolgan. Qabul so'rovida (`ReceiveSupplyInput`) esa     │
 * │ summa maydoni UMUMAN yo'q: server sxemasi `.strict()` va noma'lum        │
 * │ kalitni 400 bilan rad etadi.                                             │
 * │                                                                          │
 * │ Bu tasodif emas, talab: tovar «ular olgan narxida» kirim qilinishi       │
 * │ kerak. Agar bu yerda tahrirlanadigan narx maydoni paydo bo'lsa, biz o'z  │
 * │ tannarximizni o'zimiz yozib qo'yardik va foyda hisobi yolg'on bo'lardi   │
 * │ — hech qanday xato chiqmagan holda.                                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import { api } from './api';
import type { Page, SortDir } from './products';

/** `packages/db/src/schema/partner-supply.ts` dagi ro'yxat bilan AYNAN bir xil. */
export const PARTNER_SUPPLY_STATUSES = ['pending', 'received', 'cancelled'] as const;
export type PartnerSupplyStatus = (typeof PARTNER_SUPPLY_STATUSES)[number];

/**
 * Ro'yxat satri.
 *
 * Hujjat raqamidan boshqa BIZGA tegishli hech qanday havola yo'q va bu
 * serverning ongli qarori (`NOTICE_COLUMNS` izohi): ta'minotchining ichki
 * hujjat identifikatorlari javobga umuman chiqmaydi.
 */
export interface PartnerSupplyRow {
  readonly id: number;
  readonly docNo: string;
  readonly issuedDate: string;
  readonly currencyCode: string;
  readonly totalAmount: string;
  readonly status: string;
  /** Qabul qilingan payt. `pending` va `cancelled` da `null`. */
  readonly receivedAt: string | null;
}

export interface PartnerSupplyLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly quantity: string;
  /** Ta'minotchining narxi = BIZNING tannarximiz. Faqat ko'rsatiladi. */
  readonly unitCost: string;
  readonly lineAmount: string;
}

export interface PartnerSupplyNotice extends PartnerSupplyRow {
  readonly lines: readonly PartnerSupplyLine[];
}

export interface PartnerSupplyListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  /**
   * Berilmasa server `pending` ni oladi — «hammasi» EMAS.
   *
   * Shuning uchun ekranda «Barchasi» varianti YO'Q: uni qo'yish
   * foydalanuvchini aldardi — u butun tarixni so'raganini o'ylab, aslida
   * faqat kutilayotganlarni ko'rardi.
   */
  readonly status?: PartnerSupplyStatus | undefined;
}

/** `NOTICE_SORTABLE` (partner-supply.service.ts) bilan AYNAN bir xil oq ro'yxat. */
export const PARTNER_SUPPLY_SORTABLE = ['docNo', 'issuedDate', 'totalAmount', 'status'] as const;

export const partnerSupplyKeys = {
  all: ['partner-supply'] as const,
  list: (params: PartnerSupplyListParams) => ['partner-supply', 'list', params] as const,
  detail: (id: number) => ['partner-supply', 'detail', id] as const,
};

export function fetchPartnerSupplyList(
  params: PartnerSupplyListParams,
  signal?: AbortSignal,
): Promise<Page<PartnerSupplyRow>> {
  return api.get<Page<PartnerSupplyRow>>('/partner-supply', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      status: params.status,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchPartnerSupplyNotice(
  id: number,
  signal?: AbortSignal,
): Promise<PartnerSupplyNotice> {
  return api.get<PartnerSupplyNotice>(`/partner-supply/${String(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * Qabul so'rovi — FAQAT ombor va ixtiyoriy izoh.
 *
 * `legalEntityId` ATAYLAB yo'q. Server uni ixtiyoriy qilgan va berilmasa
 * OMBORDAN oladi (`resolveLegalEntity()`). Ekranga qo'shish taklif qilindi
 * va rad etildi: bitta yuridik shaxsli tashkilot (bizning holatimizda
 * hammasi shunday) bu maydonni umuman bilmasligi kerak, ko'p yuridik
 * shaxslida esa to'g'ri javob baribir ombordan hosila bo'ladi — ya'ni
 * tanlov faqat NOTO'G'RI javob berish imkonini qo'shardi.
 */
export interface ReceiveSupplyInput {
  readonly warehouseId: number;
  /** Server chegarasi 500 belgi — forma ham shu chegarada to'xtatadi. */
  readonly notes?: string;
}

/**
 * Yukni omborga kirim qilish.
 *
 * Javob — YANGILANGAN xabarnoma (`status: 'received'`, `receivedAt` bilan).
 * Uni tashlab yubormaslik kerak: kartochka darhol yangi holatni ko'rsatadi
 * va foydalanuvchi «bo'ldimi?» degan savol bilan qolmaydi.
 */
export function receivePartnerSupply(
  id: number,
  input: ReceiveSupplyInput,
): Promise<PartnerSupplyNotice> {
  return api.post<PartnerSupplyNotice>(`/partner-supply/${String(id)}/receive`, {
    warehouseId: input.warehouseId,
    ...(input.notes ? { notes: input.notes } : {}),
  });
}
